import { ObjectSchema, array, boolean, number, object, string } from "yup";

import { InstitutionFormData } from "@/types";

const oneClickSchema = object()
  .shape({
    sone_url: string()
      .nullable()
      .default(null)
      .transform((value) => (value === "" ? null : value))
      .test("sone-url-required", "REQUIRED_FIELD", function (value) {
        const one_click_enabled = this.from?.[1]?.value?.one_click_enabled;
        if (!one_click_enabled) {
          return true;
        }
        return value !== null && value !== "";
      }),
    one_server_institution_identifier: string()
      .nullable()
      .default(null)
      .transform((value) => (value === "" ? null : value))
      .test("institution-identifier-required", "REQUIRED_FIELD", function (value) {
        const one_click_enabled = this.from?.[1]?.value?.one_click_enabled;
        if (!one_click_enabled) {
          return true;
        }
        return value !== null && value !== "";
      }),
    one_server_username: string()
      .nullable()
      .default(null)
      .transform((value) => (value === "" ? null : value))
      .test("username-required", "REQUIRED_FIELD", function (value) {
        const one_click_enabled = this.from?.[1]?.value?.one_click_enabled;
        if (!one_click_enabled) {
          return true;
        }
        return value !== null && value !== "";
      }),
    one_server_password: string()
      .nullable()
      .default(null)
      .transform((value) => (value === "" ? null : value))
      .test("password-required", "REQUIRED_FIELD", function (value) {
        const one_click_enabled = this.from?.[1]?.value?.one_click_enabled;
        if (!one_click_enabled) {
          return true;
        }
        return value !== null && value !== "";
      }),
    one_server_password_confirm: string()
      .nullable()
      .default(null)
      .transform((value) => (value === "" ? null : value))
      .test("password-confirm-required", "REQUIRED_FIELD", function (value) {
        const one_click_enabled = this.from?.[1]?.value?.one_click_enabled;
        if (!one_click_enabled) {
          return true;
        }
        return value !== null && value !== "";
      })
      .test("passwords-match", "PASSWORDS_MUST_MATCH", function (value) {
        const { one_server_password } = this.parent;
        const one_click_enabled = this.from?.[1]?.value?.one_click_enabled;
        if (!one_click_enabled) {
          return true;
        }
        if (one_server_password) {
          return value === one_server_password;
        }
        return true;
      }),
  })
  .default(undefined);

const outlierShieldSchema = object()
  .shape({
    sone_url: string()
      .nullable()
      .default(null)
      .transform((value) => (value === "" ? null : value))
      .test("sone-url-required", "REQUIRED_FIELD", function (value) {
        const outlier_shield_enabled = this.from?.[1]?.value?.outlier_shield_enabled;
        if (!outlier_shield_enabled) {
          return true;
        }
        return value !== null && value !== "";
      }),
    one_server_institution_identifier: string()
      .nullable()
      .default(null)
      .transform((value) => (value === "" ? null : value))
      .test("institution-identifier-required", "REQUIRED_FIELD", function (value) {
        const outlier_shield_enabled = this.from?.[1]?.value?.outlier_shield_enabled;
        if (!outlier_shield_enabled) {
          return true;
        }
        return value !== null && value !== "";
      }),
    one_server_username: string()
      .nullable()
      .default(null)
      .transform((value) => (value === "" ? null : value))
      .test("username-required", "REQUIRED_FIELD", function (value) {
        const outlier_shield_enabled = this.from?.[1]?.value?.outlier_shield_enabled;
        if (!outlier_shield_enabled) {
          return true;
        }
        return value !== null && value !== "";
      }),
    one_server_password: string()
      .nullable()
      .default(null)
      .transform((value) => (value === "" ? null : value))
      .test("password-required", "REQUIRED_FIELD", function (value) {
        const outlier_shield_enabled = this.from?.[1]?.value?.outlier_shield_enabled;
        if (!outlier_shield_enabled) {
          return true;
        }
        return value !== null && value !== "";
      }),
    one_server_password_confirm: string()
      .nullable()
      .default(null)
      .transform((value) => (value === "" ? null : value))
      .test("password-confirm-required", "REQUIRED_FIELD", function (value) {
        const outlier_shield_enabled = this.from?.[1]?.value?.outlier_shield_enabled;
        if (!outlier_shield_enabled) {
          return true;
        }
        return value !== null && value !== "";
      })
      .test("passwords-match", "PASSWORDS_MUST_MATCH", function (value) {
        const { one_server_password } = this.parent;
        const outlier_shield_enabled = this.from?.[1]?.value?.outlier_shield_enabled;
        if (outlier_shield_enabled && one_server_password) {
          return value === one_server_password;
        }
        return true;
      }),
  })
  .default(undefined);

const valuationsPackageSchema = object()
  .shape({
    quota: string()
      .nullable()
      .optional()
      .transform((value) => (value === "" ? undefined : value))
      .test("quota-required", "REQUIRED_FIELD", function (value) {
        const valuations_package_enabled = this.from?.[1]?.value?.valuations_package_enabled;
        if (!valuations_package_enabled) {
          return true;
        }
        return value !== undefined && value !== "";
      })
      .test("quota-integer", "MUST_BE_INTEGER", function (value) {
        if (!value) {
          return true;
        }
        if (!/^\d+$/.test(value)) {
          return this.createError({ message: "MUST_BE_INTEGER" });
        }
        const num = parseInt(value, 10);
        if (num <= 0) {
          return this.createError({ message: "VALUE_MUST_BE_GREATER_THAN|0" });
        }
        return true;
      }),
    duration: string()
      .nullable()
      .optional()
      .transform((value) => (value === "" ? undefined : value))
      .test("duration-required", "REQUIRED_FIELD", function (value) {
        const valuations_package_enabled = this.from?.[1]?.value?.valuations_package_enabled;
        if (!valuations_package_enabled) {
          return true;
        }
        return value !== undefined && value !== "";
      }),
    sone_url: string()
      .nullable()
      .default(null)
      .transform((value) => (value === "" ? null : value))
      .test("sone-url-required", "REQUIRED_FIELD", function (value) {
        const valuations_package_enabled = this.from?.[1]?.value?.valuations_package_enabled;
        if (!valuations_package_enabled) {
          return true;
        }
        return value !== null && value !== "";
      }),
    one_server_institution_identifier: string()
      .nullable()
      .default(null)
      .transform((value) => (value === "" ? null : value))
      .test("institution-identifier-required", "REQUIRED_FIELD", function (value) {
        const valuations_package_enabled = this.from?.[1]?.value?.valuations_package_enabled;
        if (!valuations_package_enabled) {
          return true;
        }
        return value !== null && value !== "";
      }),
    one_server_username: string()
      .nullable()
      .default(null)
      .transform((value) => (value === "" ? null : value))
      .test("username-required", "REQUIRED_FIELD", function (value) {
        const valuations_package_enabled = this.from?.[1]?.value?.valuations_package_enabled;
        if (!valuations_package_enabled) {
          return true;
        }
        return value !== null && value !== "";
      }),
    one_server_password: string()
      .nullable()
      .default(null)
      .transform((value) => (value === "" ? null : value))
      .test("password-required", "REQUIRED_FIELD", function (value) {
        const valuations_package_enabled = this.from?.[1]?.value?.valuations_package_enabled;
        if (!valuations_package_enabled) {
          return true;
        }
        return value !== null && value !== "";
      }),
    one_server_password_confirm: string()
      .nullable()
      .default(null)
      .transform((value) => (value === "" ? null : value))
      .test("password-confirm-required", "REQUIRED_FIELD", function (value) {
        const valuations_package_enabled = this.from?.[1]?.value?.valuations_package_enabled;
        if (!valuations_package_enabled) {
          return true;
        }
        return value !== null && value !== "";
      })
      .test("passwords-match", "PASSWORDS_MUST_MATCH", function (value) {
        const { one_server_password } = this.parent;
        const valuations_package_enabled = this.from?.[1]?.value?.valuations_package_enabled;
        if (valuations_package_enabled && one_server_password) {
          return value === one_server_password;
        }
        return true;
      }),
  })
  .default(undefined);

export const createInstitutionSchema: ObjectSchema<InstitutionFormData> = object().shape({
  id: number().nullable().optional(),
  name: string().required("REQUIRED_FIELD"),
  nation: string()
    .nullable()
    .optional()
    .transform((value) => (value === "" ? undefined : value)),
  town: string()
    .nullable()
    .optional()
    .transform((value) => (value === "" ? undefined : value)),
  street: string()
    .nullable()
    .optional()
    .transform((value) => (value === "" ? undefined : value)),
  zip: string()
    .nullable()
    .optional()
    .transform((value) => (value === "" ? undefined : value)),
  house_number: number()
    .nullable()
    .optional()
    .transform((value) => {
      if (value === "" || value === null || value === undefined) {
        return undefined;
      }
      const numValue = Number(value);
      return isNaN(numValue) ? undefined : numValue;
    }),
  subscription_id: number().nullable().optional(),
  expiration_date: string().nullable().optional(),
  is_active: boolean().optional(),
  one_click_enabled: boolean().nullable().optional(),
  outlier_shield_enabled: boolean().nullable().optional(),
  valuations_package_enabled: boolean().nullable().optional(),
  widgets: array().nullable().optional(),
  one_click: oneClickSchema.nullable().optional(),
  outlier_shield: outlierShieldSchema.nullable().optional(),
  valuations_package: valuationsPackageSchema.nullable().optional(),
});
